import { logInsightsResponseReceived } from '../helpers/insightsEventsHelpers';
import getInsightsTraceId from '../selectors/getInsightsTraceId';
import {
    getInitialRequestTime,
    getLogicalId,
} from '../selectors/meetingInsightsInstrumentationSelectors';
import getScenarioEndpoint from '../selectors/getScenarioEndpoint';
import isFetchMeetingInsightsServicePending from '../selectors/isFetchMeetingInsightsServicePending';
import { getMeetingInsights } from '../selectors/meetingInsightsSelectors';
import fetchMeetingInsightsService from '../services/fetchMeetingInsightsService';
import {
    EmailInsightType,
    FileInsightType,
    InsightsDataFetchSource,
    convertInsightsEntrySourceEnumToName,
    RelevanceRelationship,
} from 'owa-meeting-insights-types-and-flights';
import RequestStatus from '../store/schema/RequestStatus';
import meetingInsightsCache from '../store/schema/meetingInsightsCache';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { GenericKeys } from 'owa-analytics/lib/types/DatapointEnums';
import { getDisplayDateFromEwsDate } from 'owa-datetime';
import { getGuid } from 'owa-guid';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { trace } from 'owa-trace';
import {
    getDeeplinkUrlForRelatedFile,
    getDeeplinkUrlForRelatedEmail,
} from '../selectors/getDeeplinkUrlForRelatedContent';
import {
    getRelatedFilesTimestamp,
    getRelatedEmailsTimestamp,
} from '../selectors/getRelatedContentTimestamp';
import {
    tryAddToPendingServiceQueue,
    tryRemoveFromPendingServiceQueue,
} from '../actions/internalActions';
import { getInsightsMapKey } from './getInsightsMapKey';
import { isFeatureEnabled } from 'owa-feature-flags';

export default async function fetchMeetingInsights(
    meetingId: string,
    dataFetchSource: InsightsDataFetchSource
): Promise<void> {
    if (isFetchMeetingInsightsServicePending(meetingId, dataFetchSource)) {
        // No-op if there is already a pending getEventRelatedContent service call going out
        return;
    }

    // We are repurposing traceId to represent if insights are in cache.
    //
    // If we are not using the cache mechanism, clear the entry for this meeting
    // and data source before requesting new recommendations so that we do not use
    // stale data to render new UI.
    const areInsightsCached =
        !isFeatureEnabled('cal-meeting-insights-noCache') &&
        !!getInsightsTraceId(meetingId, dataFetchSource);
    let logicalId;

    if (!areInsightsCached) {
        logicalId = getGuid();

        // if insights are not cached, write an empty entry into store. This is needed for deeplink scenario to grab the meetingId.
        // VSO https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/55184
        // Temporary workaround for deeplink insights.
        // Otherwise deeplink routes to other handler because setMeetingInsights mutator is not called synchronously
        meetingInsightsCache.add(getInsightsMapKey(meetingId, dataFetchSource), {
            files: [],
            emails: [],
            serviceRequestStatus: RequestStatus.NotInitiated,
            requestTimestamp: new Date().getTime(),
            traceId: null,
            logicalId: logicalId,
            dataFetchSource: dataFetchSource,
        });
    }

    tryAddToPendingServiceQueue(meetingId, dataFetchSource);

    // Start datapoint to track getEventRelatedContent request latency.
    const networkPerformanceDatapoint = new PerformanceDatapoint('RelatedContentService', {
        sessionSampleRate: 20,
    });
    networkPerformanceDatapoint.addCosmosOnlyData(
        JSON.stringify({
            clientRequestId: logicalId,
            meetingId: meetingId,
        })
    );

    // Issue fetchMeetingInsights service call
    const response = await fetchMeetingInsightsService(
        meetingId,
        logicalId,
        getScenarioEndpoint(dataFetchSource)
    );

    const dataFetchSourceName = convertInsightsEntrySourceEnumToName(dataFetchSource);

    // End datapoint to track fetchMeetingInsights request latency.
    networkPerformanceDatapoint.addCosmosOnlyData(
        JSON.stringify({
            statusCode: response.status,
            dataFetchSource: dataFetchSourceName,
        })
    );

    // Remove the meeting for scenario from the pending service queue
    tryRemoveFromPendingServiceQueue(meetingId, dataFetchSource);

    // 2. Load the data to store if service call succeeds. If the service call fails, load the data only when client doesn't have a successful response.
    if (isSuccessStatusCode(response.status)) {
        // Success
        onFetchInsightsSucceeded(response, meetingId, dataFetchSource, networkPerformanceDatapoint);
    } else {
        // Failure
        onFetchInsightsFailed(response, meetingId, dataFetchSource, networkPerformanceDatapoint);
        throw new Error('FetchInsights failed, dataFetchSource: ' + dataFetchSourceName);
    }
}

async function onFetchInsightsSucceeded(
    response: any,
    meetingId: string,
    dataFetchSource: InsightsDataFetchSource,
    networkPerformanceDatapoint: PerformanceDatapoint
) {
    let parsedResponse;
    const logicalId = getLogicalId(meetingId, dataFetchSource);
    try {
        const responseBody = await response.text();
        parsedResponse = JSON.parse(responseBody);
    } catch (e) {
        trace.warn('meetingPrep: ' + e.toString());

        // Consider this as a failed response and process it
        processFailedResponse(
            meetingId,
            logicalId /* traceId */, // use logicalId in absence of traceId
            null /* status */,
            dataFetchSource,
            networkPerformanceDatapoint
        );
        return;
    }

    networkPerformanceDatapoint.end();

    const rawSuggestions = parsedResponse.EntitySets[0].ResultSets[0].Results;
    const { files, emails } = convertToClientData(rawSuggestions);
    const traceId = getServiceTraceId(parsedResponse);
    const requestTimestamp = getInitialRequestTime(meetingId, dataFetchSource);

    logInsightsResponseReceived(
        meetingId,
        traceId,
        <number>networkPerformanceDatapoint.getData(GenericKeys.e2eTimeElapsed) /* latency */,
        response.status /* statusCode */,
        dataFetchSource
    );

    const areInsightsCachedWithValidTraceId = !!getInsightsTraceId(meetingId, dataFetchSource);
    const containsNewContent =
        !areInsightsCachedWithValidTraceId ||
        !isResponseSameAsCachedData(meetingId, files, emails, dataFetchSource);

    // Only update the cache if the result is updated
    if (isFeatureEnabled('cal-meeting-insights-noCache') || containsNewContent) {
        // Load related files and emails to store
        // VSO https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/55184
        // Temporary workaround for deeplink insights.
        // Otherwise deeplink routes to other handler because setMeetingInsights mutator is not called synchronously
        meetingInsightsCache.add(getInsightsMapKey(meetingId, dataFetchSource), {
            files: files,
            emails: emails,
            serviceRequestStatus: RequestStatus.Complete,
            requestTimestamp: requestTimestamp,
            traceId: traceId,
            logicalId: logicalId,
            dataFetchSource: dataFetchSource,
        });
    }

    trace.info(
        'insights: fetchMeetingInsights succeeds for meetingId=' +
            meetingId +
            ', traceId=' +
            traceId +
            ', containsNewContent=' +
            containsNewContent
    );
}

async function onFetchInsightsFailed(
    response: Response,
    meetingId: string,
    dataFetchSource: InsightsDataFetchSource,
    networkPerformanceDatapoint: PerformanceDatapoint
) {
    let failedJsonResponse;
    try {
        const responseText = await response.text();
        if (typeof responseText === 'object') {
            failedJsonResponse = await JSON.parse(responseText);
        }
    } catch (e) {
        trace.warn(
            'fetchInsightsFailed with ' +
                response.status +
                ', dataFetchSource: ' +
                dataFetchSource +
                ', ' +
                e.toString()
        );
    }

    processFailedResponse(
        meetingId,
        failedJsonResponse?.traceId, // failedJsonResponse is undefined when recommendation failed with response of plain text
        failedJsonResponse?.status,
        dataFetchSource,
        networkPerformanceDatapoint
    );
}

function processFailedResponse(
    meetingId: string,
    traceId: string | undefined,
    status: string | undefined,
    dataFetchSource: InsightsDataFetchSource,
    networkPerformanceDatapoint: PerformanceDatapoint
) {
    networkPerformanceDatapoint.endWithError(DatapointStatus.ServerError);

    const insights = getMeetingInsights(meetingId, dataFetchSource);
    const logicalId = getLogicalId(meetingId, dataFetchSource);

    // Log ResponseReceived even when recommendation service call failed
    logInsightsResponseReceived(
        meetingId,
        !!traceId ? traceId : logicalId, // in case there is no trace id available, client should use the ClientRequestId as trace id
        <number>networkPerformanceDatapoint.getData(GenericKeys.e2eTimeElapsed) /* latency */,
        status,
        dataFetchSource
    );

    if (insights && insights.serviceRequestStatus === RequestStatus.Complete) {
        // No-op if current service call failed, but client has cached successful response
        return;
    }

    // Load the failed data to client store only when client does not have a successful response
    // VSO https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/55184
    // Temporary workaround for deeplink insights.
    // Otherwise deeplink routes to other handler because setMeetingInsights mutator is not called synchronously
    meetingInsightsCache.add(getInsightsMapKey(meetingId, dataFetchSource), {
        files: [],
        emails: [],
        serviceRequestStatus: RequestStatus.Error,
        requestTimestamp: getInitialRequestTime(meetingId, dataFetchSource),
        logicalId: logicalId,
        traceId: traceId,
        dataFetchSource: dataFetchSource,
    });
}

function convertToClientData(
    rawSuggestions: any[]
): { files: FileInsightType[]; emails: EmailInsightType[] } {
    const fileKeyMap = {};
    const files: FileInsightType[] = rawSuggestions
        ? rawSuggestions
              .filter(x => x.Type === 'Document')
              .map(x => convertToClientFileData(x, fileKeyMap))
              .filter(x => !!x)
        : [];
    const emails: EmailInsightType[] = rawSuggestions
        ? rawSuggestions.filter(x => x.Type === 'Message').map(x => convertToClientEmailData(x))
        : [];
    return { files, emails };
}

function convertToClientFileData(rawSuggestion: any, fileKeyMap: {}): FileInsightType | null {
    const contentSource = rawSuggestion.ContentSource
        ? rawSuggestion.ContentSource
        : rawSuggestion.Provenance;

    const source = rawSuggestion.Source;
    const lastShared = source.LastShared;
    let attachmentId;
    let messageId;
    let sharingEntityType;
    let isSharedOverEmail = false;
    let isMeetingAttachment = false;
    let sharedDateTime;
    let sharingSubject;

    const { senderName, senderEmailAddress } = getSenderInfo(
        lastShared,
        source.LastModifiedBy,
        source.Author
    );

    // lastShared is null for implicit suggestion
    if (lastShared) {
        attachmentId = lastShared.AttachmentId;
        messageId = lastShared.MessageId;
        sharedDateTime = getDisplayDateFromEwsDate(lastShared.SharedDateTime);
        sharingSubject = lastShared.SharingSubject;
        sharingEntityType = lastShared.SharingEntityType;
        isSharedOverEmail =
            sharingEntityType != null &&
            (sharingEntityType.toLowerCase() === 'email' ||
                sharingEntityType.toLowerCase() === 'meetinginvite');
        isMeetingAttachment =
            sharingEntityType != null && sharingEntityType.toLowerCase() === 'meetinginvite';

        if (isSharedOverEmail) {
            // De-dup meeting attachment by file name, extension and sender smtp as a temp workaround
            // Remove when bug is fixed https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/56113
            const insightsFileKey = source.FileName + ':' + senderEmailAddress;
            if (!!fileKeyMap[insightsFileKey]) {
                return null;
            }
            fileKeyMap[insightsFileKey] = true;
        }
    }

    const hasExchangeContentSource =
        contentSource != null && contentSource.toLowerCase() === 'exchange';
    const isCloudyFile = !hasExchangeContentSource && isSharedOverEmail;
    const fileDeeplinkUrl = getDeeplinkUrlForRelatedFile(
        messageId,
        attachmentId,
        source.WebUrl ? source.WebUrl : source.Url,
        isCloudyFile
    );
    const lastActivityTimestamp = source.LastModifiedDateTime
        ? getDisplayDateFromEwsDate(source.LastModifiedDateTime)
        : null;
    const { timestamp, lastActivityType } = getRelatedFilesTimestamp(
        isSharedOverEmail,
        sharedDateTime,
        lastActivityTimestamp
    );

    return {
        fileExtension: source.FileExtension,
        fileFullName: source.FileName,
        fileTitle: source.Title,
        fileUrl: source.Url,
        timestamp: timestamp,
        lastActivityType: lastActivityType,
        isCloudyFile: isCloudyFile,
        isMeetingAttachment: isMeetingAttachment,
        reactDeeplinkUrl: fileDeeplinkUrl,
        referenceId: rawSuggestion.ReferenceId,
        relevanceRelationship: convertRelevanceRelationshipToEnum(
            rawSuggestion.SubstrateSearchExtendedProperties.RelevanceRelationship
        ),
        senderEmailAddress: senderEmailAddress,
        senderName: senderName,
        sharingSubject: sharingSubject,
        source: contentSource,
        type: rawSuggestion.Type,
        reasonMarker: rawSuggestion.SubstrateSearchExtendedProperties.ReasonMarker,
        reasonShortCode: rawSuggestion.SubstrateSearchExtendedProperties.ReasonShortCode,
    };
}

function convertToClientEmailData(rawSuggestion: any): EmailInsightType {
    const source = rawSuggestion.Source;
    const from = source.From;
    const { attachmentMessageId, attachmentId, isMeetingAttachment } = getAttachmentInfo(
        rawSuggestion.Id
    );
    const emailDeeplinkUrl =
        !!attachmentMessageId && !!attachmentId
            ? getDeeplinkUrlForRelatedEmail(attachmentId, attachmentMessageId)
            : null;
    return {
        conversationId: source.ConversationId ? source.ConversationId.Id : null,
        hasAttachments: source.HasAttachments,
        importance: source.Importance,
        isMeetingAttachment: isMeetingAttachment,
        isRead: source.IsRead,
        itemId: source.ItemId ? source.ItemId.Id : null,
        preview: source.Preview,
        reactDeeplinkUrl: emailDeeplinkUrl,
        referenceId: rawSuggestion.ReferenceId,
        relevanceRelationship: convertRelevanceRelationshipToEnum(
            rawSuggestion.SubstrateSearchExtendedProperties.RelevanceRelationship
        ),
        senderEmailAddress: from ? from.EmailAddress.Address : '',
        senderName: from ? from.EmailAddress.Name : '',
        sentTimestamp: getRelatedEmailsTimestamp(getDisplayDateFromEwsDate(source.DateTimeSent)),
        subject: source.Subject,
        type: rawSuggestion.Type,
        reasonMarker: rawSuggestion.SubstrateSearchExtendedProperties.ReasonMarker,
        reasonShortCode: rawSuggestion.SubstrateSearchExtendedProperties.ReasonShortCode,
    };
}

function getServiceTraceId(parsedResponse: any): string | null {
    if (parsedResponse?.Instrumentation) {
        return parsedResponse.Instrumentation.TraceId;
    }
    return null;
}

function convertRelevanceRelationshipToEnum(relevanceRelationship: string): number {
    switch (relevanceRelationship) {
        case 'Explicit':
            return RelevanceRelationship.Explicit;

        case 'Implicit':
            return RelevanceRelationship.Implicit;

        default:
            return RelevanceRelationship.None;
    }
}

function getSenderInfo(
    lastShared: any,
    lastModified: any,
    author: any
): { senderName: string; senderEmailAddress: string } {
    // Choose the sender based on priority, i.e. lastShared, lastModified, author
    let sender;
    if (lastShared?.SharedBy) {
        sender = lastShared.SharedBy;
    } else if (lastModified) {
        sender = lastModified;
    } else {
        sender = author;
    }

    let name = sender ? sender.DisplayName : '';
    let email = sender?.EmailAddresses ? sender.EmailAddresses[0].Address : '';

    return { senderName: name, senderEmailAddress: email };
}

function getAttachmentInfo(
    suggestionId: string
): {
    attachmentMessageId: string;
    attachmentId: string;
    isMeetingAttachment: boolean;
} {
    const defaultAttachmentProps = {
        attachmentMessageId: null,
        attachmentId: null,
        isMeetingAttachment: false,
    };
    // Convert the search result id to item id and attachment id
    if (!suggestionId) {
        return defaultAttachmentProps;
    }

    const parts = suggestionId.split(':');
    if (
        parts.length != 3 ||
        (parts[0].toLowerCase() !== 'ME'.toLowerCase() &&
            parts[0].toLowerCase() !== 'EV'.toLowerCase()) ||
        !parts[1] ||
        !parts[2]
    ) {
        return defaultAttachmentProps;
    }

    // Make sure the parsed data meets the server client-server contract
    // An example of id
    // "ME:ATTACHMENT_MESSAGE_ID:ATTACHMENT_ID", or "EV:ATTACHMENT_MESSAGE_ID:ATTACHMENT_ID" if it's meeting attachment
    return {
        attachmentMessageId: parts[1],
        attachmentId: parts[2],
        isMeetingAttachment: parts[0].toLowerCase() === 'EV'.toLowerCase(),
    };
}

function isResponseSameAsCachedData(
    meetingId: string,
    files: FileInsightType[],
    emails: EmailInsightType[],
    dataFetchSource: InsightsDataFetchSource
): boolean {
    const insightsFromCache = getMeetingInsights(meetingId, dataFetchSource);

    if (!insightsFromCache) {
        return false;
    }

    if (
        files.length != insightsFromCache.files.length ||
        emails.length != insightsFromCache.emails.length
    ) {
        return false;
    }

    for (let index = 0; index < files.length; index++) {
        if (files[index].reactDeeplinkUrl != insightsFromCache.files[index].reactDeeplinkUrl) {
            return false;
        }
    }

    for (let index = 0; index < emails.length; index++) {
        if (emails[index].itemId != insightsFromCache.emails[index].itemId) {
            return false;
        }
    }

    return true;
}
